import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, CreditCard, Ban } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Logo } from "@/components/Logo";
import { LangToggle } from "@/components/LangToggle";
import { StatusBadge } from "@/components/StatusBadge";
import { PaymentOptions } from "@/components/PaymentOptions";
import { Loader } from "@/components/Loader";

export default function Status() {
  const { user, t, logout, refresh } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (user?.role === "admin") { navigate("/admin", { replace: true }); return; }
    if (user?.status === "active") { navigate("/app/home", { replace: true }); return; }
    api.get("/membership").then(({ data }) => setData(data)).catch(() => {});
  }, [user, navigate]);

  const signOut = async () => { await logout(); navigate("/", { replace: true }); };

  if (!user) return null;
  const status = user.status;

  const meta = {
    application_pending: { icon: Clock, title: t("status_pending_title"), body: t("status_pending_body") },
    approved_payment_pending: { icon: CreditCard, title: t("status_payment_title"), body: t("status_payment_body") },
    suspended: { icon: Ban, title: t("status_suspended_title"), body: t("status_suspended_body") },
    expired: { icon: CreditCard, title: t("status_expired_title"), body: t("status_expired_body") },
  }[status] || { icon: Clock, title: "", body: "" };

  const Icon = meta.icon;

  return (
    <div className="min-h-screen flex flex-col">
      <header className="flex items-center justify-between px-6 pt-6">
        <Logo size="sm" />
        <div className="flex items-center gap-4">
          <LangToggle />
          <button onClick={signOut} data-testid="signout-button" className="eyebrow hover:text-[color:var(--cu-gold-light)]">{t("sign_out")}</button>
        </div>
      </header>

      <div className="flex-1 px-6 py-10 max-w-md mx-auto w-full fade-up">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full gold-border mb-6" style={{ background: "rgba(212,175,55,0.07)" }}>
            <Icon size={26} className="text-[color:var(--cu-gold)]" />
          </div>
          <h1 className="font-serif-cu text-2xl mb-3">{meta.title}</h1>
          <div className="flex justify-center mb-5"><StatusBadge status={status} /></div>
          <p className="text-sm text-[color:var(--cu-muted)] leading-relaxed">{meta.body}</p>
          <p className="text-sm text-[color:var(--cu-text)] mt-4 font-serif-cu">{user.first_name} {user.surname}</p>
        </div>

        {(status === "approved_payment_pending" || status === "expired") && data && (
          <div className="mt-10">
            <PaymentOptions member={data.member} payment={data.payment} onNotified={refresh} />
          </div>
        )}
      </div>
    </div>
  );
}
